import { Paper, TablePagination } from '@mui/material';
import Grid from '@mui/material/Grid';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TagService } from './services/tag.service';
import { TagResponse } from './services/responses/tag.response';
import { TagHook } from './hooks/tag.hook';
import { TagFilter } from './components/tag.filter';
import { TagTable } from './components/tag.table';
import { TagModal } from './components/tag.modal';

/// Aba de Configurações gerais: tags se cadastram junto das outras listas que classificam o lead.
export const TagsPanel = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const { sidenavColor } = controller;
    const service = new TagService();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderDirection, setOrderDirection] = useState<'desc' | 'asc'>('asc');
    const [nameFilter, setNameFilter] = useState('');
    const [filterFinal, setFilterFinal] = useState('');
    const [refresh, setRefresh] = useState(true);
    const [total, setTotal] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [editItem, setEditItem] = useState<TagResponse | null>(null);

    const { json } = new TagHook({
        page,
        limit: rowsPerPage,
        orderBy: 'Name',
        orderDirection,
        filter: filterFinal,
        refresh,
        service,
    }).getJson();

    useEffect(() => {
        setTotal(json?.data?.totalItens ?? 0);
    }, [json]);

    const searchFilter = () => {
        setPage(0);
        setFilterFinal(nameFilter ? `?search=${encodeURIComponent(nameFilter)}` : '');
    };

    const clearSearch = () => {
        setNameFilter('');
        setFilterFinal('');
        setRefresh((p) => !p);
    };

    const getAll = () => setRefresh((p) => !p);

    const handleOrderList = () => setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));

    const handleModal = () => {
        if (openModal) setEditItem(null);

        setOpenModal((p) => !p);
    };

    const getById = async (id: string) => {
        const res = await service.getById(id);

        setEditItem(res.data.data);
        setOpenModal(true);
    };

    return (
        <>
            <Grid container spacing={1}>
                <TagFilter
                    nameFilter={nameFilter}
                    setNameFilter={setNameFilter}
                    onSearch={searchFilter}
                    onClear={clearSearch}
                    onEnter={(e) => e.key === 'Enter' && searchFilter()}
                />
                <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <MDButton
                        variant="gradient"
                        color={sidenavColor}
                        onClick={handleModal}
                        sx={{ height: 40, minWidth: 130, whiteSpace: 'nowrap' }}
                    >
                        {t('general.adicionar')}
                    </MDButton>
                </Grid>
            </Grid>
            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                <TagTable
                    data={json}
                    editAction={getById}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    orderDirection={orderDirection}
                    handleOrderList={handleOrderList}
                />
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage={t('general.linhasPorPagina')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('general.X-de-X')} ${count !== -1 ? count : `${t('general.maisQue')} ${to}`}`
                    }
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value));
                        setPage(0);
                    }}
                />
            </Paper>
            <TagModal
                open={openModal}
                edit={editItem}
                handleModal={handleModal}
                getAll={getAll}
                clearSearch={clearSearch}
            />
        </>
    );
};
